/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
// The click handlers below only call stopPropagation, keeping a click on a phone /
// email link or the row actions from also opening the row's edit dialog. They add no
// interactive behaviour of their own, so there is nothing to bind a key handler to.
'use client'

import { ColumnDef } from '@tanstack/react-table'
import { MailIcon, PhoneIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Contact } from '@/api/hooks/useContacts'
import { useDeleteContact } from '@/api/hooks/useDeleteContact'
import { ColumnActions } from '@/components/organisms/data-table/actions-column'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIndividuals } from '@/hooks/useIndividuals'

import { ConfirmDialog } from '../confirm-dialog'

type Options = {
  onEdit(contact: Contact): void
}

export const useContactsTableColumns: (options: Options) => ColumnDef<Contact>[] = ({ onEdit }) => {
  const { selectedIndividualId } = useIndividuals()
  const deleteContact = useDeleteContact()
  const { t } = useTranslation()

  const onRequestDelete = (contactId: string) => {
    if (!selectedIndividualId) return
    deleteContact.mutate({ contactId, individualId: selectedIndividualId })
  }

  return [
    {
      accessorKey: 'name',
      cell: ({ row }) => {
        const { name, organization } = row.original

        return (
          <div className="min-w-0">
            <div className="font-medium line-clamp-1">{name}</div>
            {organization && (
              <div className="text-text-secondary text-xs line-clamp-1">{organization}</div>
            )}
          </div>
        )
      },
      header: t('contactsTable.columns.name'),
      // Large finite width so auto table-layout gives the name column the slack and
      // shrinks the trailing columns to their content — same trick as notes-table.
      size: 9999,
    },
    {
      accessorKey: 'role',
      cell: ({ row }) => {
        const { originalRole, role } = row.original

        if (!role && !originalRole) {
          return <span className="text-text-secondary">–</span>
        }

        // Show the generalized category, not originalRole. originalRole is whatever the
        // source document happened to write, so it is inconsistently cased and often
        // abbreviated or overlong ("specialistläkare", "Arb.ter"). The category is
        // curated and translated, so the column reads uniformly; the document's own
        // wording moves to the tooltip, where the extra precision is still available.
        // Falls back to originalRole only when the AI could not assign a category.
        //
        // Dynamic enum key — cast to one concrete member so i18next's key typing
        // accepts it, same as the issuer column in documents-table.
        const categoryLabel = role
          ? t(('funkityContactRole_' + role) as 'funkityContactRole_physician')
          : undefined

        // Plain text, not a Badge: in this codebase a Badge marks a *status* (see the
        // "Invited" pill in users-table), and issuer roles in documents-table render as
        // plain secondary text. A role is an attribute of the person, not a state.
        const label = <span className="line-clamp-1">{categoryLabel ?? originalRole}</span>

        // Skip the tooltip when it would only repeat the label.
        const detail =
          originalRole && originalRole.toLowerCase() !== (categoryLabel ?? '').toLowerCase()
            ? originalRole
            : undefined

        if (!detail) return label

        return (
          <Tooltip>
            <TooltipTrigger asChild>{label}</TooltipTrigger>
            <TooltipContent>{detail}</TooltipContent>
          </Tooltip>
        )
      },
      header: t('contactsTable.columns.role'),
    },
    {
      cell: ({ row }) => {
        const { email, phone } = row.original

        if (!email && !phone) return <span className="text-text-secondary">–</span>

        return (
          <div className="flex flex-col gap-1 text-sm" onClick={e => e.stopPropagation()}>
            {phone && (
              <a className="flex items-center gap-1.5 hover:underline" href={`tel:${phone}`}>
                <PhoneIcon className="size-3.5 text-text-secondary" />
                {phone}
              </a>
            )}
            {email && (
              <a className="flex items-center gap-1.5 hover:underline" href={`mailto:${email}`}>
                <MailIcon className="size-3.5 text-text-secondary" />
                <span className="line-clamp-1">{email}</span>
              </a>
            )}
          </div>
        )
      },
      header: t('contactsTable.columns.contactDetails'),
      id: 'contactDetails',
    },
    {
      accessorKey: 'sourceCount',
      cell: ({ row }) => {
        const { sourceCount, sources } = row.original

        if (!sourceCount) return <div className="text-text-secondary text-right">–</div>

        return (
          <div className="text-right" onClick={e => e.stopPropagation()}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-text-secondary">{sourceCount}</span>
              </TooltipTrigger>
              <TooltipContent>
                <ul className="max-w-xs">
                  {sources.map(source => (
                    <li className="line-clamp-1" key={source.documentId}>
                      {source.documentName ?? source.documentId}
                    </li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
          </div>
        )
      },
      header: () => <div className="text-right">{t('contactsTable.columns.sources')}</div>,
    },
    {
      cell: ({ row }) => {
        const [deleteOpen, setDeleteOpen] = useState(false)

        return (
          <ColumnActions
            dropDownMenuContent={
              <>
                <DropdownMenuItem onSelect={() => onEdit(row.original)}>
                  {t('contactsTable.rowActionsDropdown.editContact')}
                </DropdownMenuItem>
                <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
                  {t('contactsTable.rowActionsDropdown.deleteContact')}
                </DropdownMenuItem>
              </>
            }
            showClickableButton={false}
          >
            <ConfirmDialog
              onConfirm={() => onRequestDelete(row.original.id)}
              onOpenChange={setDeleteOpen}
              open={deleteOpen}
              variant="destructive"
            />
          </ColumnActions>
        )
      },
      columnClassName: 'w-px p-2 @3xl/table:p-5',
      enableHiding: false,
      id: 'actions',
    },
  ]
}
